import { useCallback, useMemo } from 'react';
import { SpaceKind } from 'shared';

import { VaultHooks } from '../../../../hooks/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import {
  BrowserNodeActions,
  SpaceBrowserCrumb,
  SpaceBrowserNode,
} from '../../types/data-room.types';

type Props = {
  breadcrumbs: SpaceBrowserCrumb[];
  // Off for every other space, which hands its files over through its own routes.
  enabled: boolean;
  // The stored link of each file in the open folder, which a rename sends back.
  fileLinks: Map<string, string>;
  folderId: null | string;
  // Matches from a search carry no stored link, so they can be opened and saved but not edited.
  isSearching: boolean;
  // A Stakeholder's Vault: files are opened and saved, never renamed or deleted.
  readOnly?: boolean;
  spaceId: string;
};

const STAKEHOLDERS_FOLDER = 'Stakeholders';
const DOCUMENTS_FOLDER = 'Documents';

// As on the Vault page: under Stakeholders the files are each holder's own, as issued, and stay
// as they are — except in a Documents folder.
const isEditablePath = (breadcrumbs: SpaceBrowserCrumb[]): boolean => {
  const names = breadcrumbs.map(({ name }) => name);

  return !names.includes(STAKEHOLDERS_FOLDER) || names.includes(DOCUMENTS_FOLDER);
};

/**
 * What a row of the Vault can do, through the Vault's own routes, with the Vault page's rules:
 * a file opens in its own tab or is saved, a folder is saved as a zip, and a file is renamed or
 * deleted where the Vault allows it. Every handler is absent outside the Vault.
 */
export const useVaultFileActions = ({
  breadcrumbs,
  enabled,
  fileLinks,
  folderId,
  isSearching,
  readOnly = false,
  spaceId,
}: Props) => {
  const openModal = DataRoomModalStore(({ openModal }) => openModal);
  const { viewFile } = VaultHooks.useViewFile();
  const { downloadVaultFile } = VaultHooks.useDownloadVaultFile();
  const { mutateFolderExport } = VaultHooks.useFolderExport();

  const openFile = useCallback((fileId: string) => viewFile({ fileId }), [viewFile]);
  const downloadFile = useCallback(
    (fileId: string) => downloadVaultFile({ fileId }),
    [downloadVaultFile],
  );
  const downloadFolder = useCallback(
    (id: string) => mutateFolderExport({ folderId: id }),
    [mutateFolderExport],
  );

  const renameFile = useCallback(
    ({ id, name }: SpaceBrowserNode) => {
      const link = fileLinks.get(id);

      if (!folderId || link === undefined) return;

      openModal({
        metadata: { spaceId, vaultFile: { folderId, id, link, name } },
        type: DataRoomModalType.RENAME_VAULT_FILE,
      });
    },
    [fileLinks, folderId, openModal, spaceId],
  );
  // The same confirmation every delete in the browser asks for; the Vault's route is chosen there.
  const deleteFile = useCallback(
    (node: SpaceBrowserNode) =>
      openModal({
        metadata: { node: { ...node, spaceKind: SpaceKind.VAULT }, spaceId },
        type: DataRoomModalType.DELETE_SPACE_NODE,
      }),
    [openModal, spaceId],
  );

  const canEdit = !readOnly && !isSearching && !!folderId && isEditablePath(breadcrumbs);

  // One object for as long as its handlers hold, so a memoised table is not redrawn for nothing.
  return useMemo<BrowserNodeActions>(
    () =>
      enabled
        ? {
            onDeleteFile: canEdit ? deleteFile : undefined,
            onDownloadFile: downloadFile,
            onDownloadFolder: downloadFolder,
            onOpenFile: openFile,
            onRenameFile: canEdit ? renameFile : undefined,
          }
        : {},
    [canEdit, deleteFile, downloadFile, downloadFolder, enabled, openFile, renameFile],
  );
};
